import { theme } from '../../../theme';
import { Button, Grid,Box,Typography,SvgIcon, IconButton, useMediaQuery } from '@mui/material';
import {  File06,  Trash01 } from '../../../assets';  
import { useNavigate } from 'react-router-dom';
import { ReportDto } from '../../../app/services/api/generated';
import { useContext, useEffect, useState } from 'react';
import useNswagClient from '../../../hooks/api/useNswagClient';
import useLogError from '../../../hooks/useLogError'; 
import DeleteReport from '../modals/DeleteReport';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { useTranslation } from 'react-i18next';
import GenerateReport from '../modals/GenerateReport'; 
import { UserContext } from '../../../components/shared/useUser';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
interface CustomReportTabProps {
  userId?: number;
  readOnly?: boolean;
}

const CustomReportTab = ({ userId, readOnly = false }: CustomReportTabProps) => { 
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { getReports,getDuplicateReportData } = useNswagClient();
  const { logError } = useLogError(); 

  const [allReports, setAllReports] = useState<ReportDto[]>(); 
  const [reportId, setReportId] = useState<number>(); 
  const [openDelete, setOpenDelete] = useState<boolean>(false); 
  const [openGenerate, setOpenGenerate] = useState<boolean>(false); 
  const[ name, setName ] = useState<string>('');
  const[ refreshPage, setRefreshPage ] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { selectedStore } = useContext(UserContext); 
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);  
  const [snackBarSeverity, setSnackBarSeverity] = useState<'error' | 'success'>('error');
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  useEffect(() => { 
    if (userId === undefined) {
      setAllReports([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    getReportsList(); 
  }, [refreshPage, userId]); 

  const handleUpdateReport = (id: number) => {
    if (userId) {
      localStorage.setItem('selectedReportUserId', String(userId));
    }
    navigate(`/reporting/create-report/${id}`, {
      state: { readOnly },
    }); 
  };

  const handleDuplicateReport = async (id: number) => {
    if (userId) {
      localStorage.setItem('selectedReportUserId', String(userId));
    }
   
    getDuplicateReportData(id, selectedStore?.storeNumber ).then((result) => {
      if (result.data) {  
        navigate('/reporting/create-report/0', {
          state: {
            duplicatedReport: result.data,   
            isDuplicated: true, 
          },
        });
      }
    })
      .catch((error) => {
        logError(error);
        setSnackBarProps('error', error); 
      });  
  
  };   

  const handleGenerate = (id: number) => { 
    setReportId(id);
    setOpenGenerate(true);
  }; 

  const getReportsList = () => {  
    getReports(selectedStore?.storeNumber, userId)
      .then((result) => {
        if (result?.data) { 
          setAllReports(result.data.reports);
        }
      })
      .catch((error) => {
        logError(error);
      }).finally(() => {
        setLoading(false);
      });
  };

  const deleteReport = (reportId: number, reportName: string) =>{ 
    setReportId(reportId);
    setOpenDelete(true);
    setName(reportName);
  };

  const handleClose = () => { 
    setOpenDelete(false);
    setOpenGenerate(false);
  };

  const handleDeleteSuccess = () => {
    setOpenDelete(false); 
    setRefreshPage(!refreshPage);
  };
  
  const handleGenerateSuccess = () => {  
    setOpenGenerate(false);  
  };
  const closeSnackBar = () => {
    setIsSnackBarOpen(false);
  };

  const setSnackBarProps = (severity: 'error'  | 'success' , message: string) => { 
    setIsSnackBarOpen(true);
    setSnackBarSeverity(severity);
    setSnackbarMessage(message);
  };

  return (    
    loading? <LoadingWheel/> : (
      <><Grid container
        spacing={2}>
        {allReports && allReports.length > 0 ? (
          allReports?.map((report) => (
            <Grid item
              xs={12}
              key={report.id}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  width: '100%',
                  p: '12px',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  justifyContent: isMobile ? 'flex-start' : 'space-between',
                  mb: '12px',
                  border: `solid 1px ${theme.palette.custom.gray[200]}`,
                  borderRadius: '4px',
                  gap: isMobile ? '8px' : '0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
                  <SvgIcon sx={{ width: '20px', mr: '12px', flexShrink: 0 }}>
                    <File06 />
                  </SvgIcon>
                  <Typography
                    variant="textMD"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flexGrow: 1,
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    {report.reportName}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    justifyContent: isMobile ? 'flex-start' : 'flex-end',
                    gap: '8px',
                    width: isMobile ? '100%' : 'auto',
                  }}
                > 
                  {!readOnly && (
                    <Button size="lg"
                      variant="secondary"
                      onClick={() => handleDuplicateReport(report.id!)}>
                      Duplicate 
                    </Button>
                  )}
                  <Button size="lg"
                    variant="secondary"
                    onClick={() => handleUpdateReport(report.id!)}>
                    {t('reporting.edit')}
                  </Button>
                  {!readOnly && (
                    <Button size="lg"
                      onClick={() => handleGenerate(report.id!)}>
                      {t('reporting.generate')}
                    </Button>
                  )}
                  {!readOnly && (
                    <IconButton onClick={() => deleteReport(report.id!, report.reportName!)}>
                      <Trash01 color={theme.palette.error[500]}
                        className="delete-icon" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Grid>
          ))
        ) : (
          <Grid item
            xs={12}>
            <Typography variant="textMD">
              {userId === undefined ? t('reporting.no-user-selected') : t('noData')}
            </Typography>
          </Grid>
        )}
      </Grid><DeleteReport
        open={openDelete}
        onClose={handleClose}
        onConfirm={handleDeleteSuccess}
        reportId={reportId ?? 0}
        name={name} />
        
      <GenerateReport
        open={openGenerate}
        onClose={handleClose}
        onConfirm={handleGenerateSuccess}
        reportId={reportId ?? 0}
      />
      <MessagesSnackbar onClose={closeSnackBar}
        open={isSnackBarOpen}
        severity={snackBarSeverity}
        message={snackbarMessage}
        duration={null}/>
      
      </> 
      
    ) 
   
  );
};
  

export default CustomReportTab;